// src/services/routeAnalyticsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { 
  OMNISTACK_BASE_URL, 
  getPrepareHeaders 
} from '@/hooks/common/common-functions'

export const routeAnalyticsApi = createApi({
  reducerPath: 'routeAnalyticsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${OMNISTACK_BASE_URL}business/route-analytics`,
    prepareHeaders: (headers) => {
      getPrepareHeaders({
        headers,
        isClientApiKey: true,
      });
      
      const businessApiKey = localStorage.getItem('businessApiKey');
      if (businessApiKey) {
        headers.set('business-x-api-key', businessApiKey);
      }
      
      return headers;
    },
  }),
  tagTypes: ['RouteReports', 'PerformanceMetrics', 'AnalyticsTrends'],
  endpoints: (builder) => ({
    // Generate route report
    generateRouteReport: builder.mutation({
      query: (reportData) => ({
        url: '/reports/generate',
        method: 'POST',
        body: reportData
      }),
      invalidatesTags: ['RouteReports']
    }),

    // Get performance metrics
    getPerformanceMetrics: builder.query({
      query: ({ businessId, timeframe }) => ({
        url: '/performance',
        params: { businessId, timeframe }
      }),
      providesTags: ['PerformanceMetrics']
    }),

    // Calculate cost savings
    calculateCostSavings: builder.query({
      query: ({ businessId, comparisonPeriod }) => ({
        url: '/cost-savings',
        params: { businessId, comparisonPeriod }
      }),
      providesTags: ['PerformanceMetrics']
    }),

    // Get efficiency trends
    getEfficiencyTrends: builder.query({
      query: ({ businessId, timeframe }) => ({
        url: '/efficiency-trends',
        params: { businessId, timeframe }
      }),
      providesTags: ['AnalyticsTrends']
    }),

    // Export analytics
    exportAnalytics: builder.mutation({
      query: ({ businessId, format, timeframe }) => ({
        url: '/export',
        params: { businessId, format, timeframe },
        responseHandler: (response) => response.blob()
      })
    }),

    // Get dashboard summary
    getDashboardSummary: builder.query({
      query: ({ businessId }) => ({
        url: '/dashboard',
        params: { businessId }
      }),
      providesTags: ['PerformanceMetrics', 'AnalyticsTrends']
    })
  })
});

export const {
  useGenerateRouteReportMutation,
  useGetPerformanceMetricsQuery,
  useCalculateCostSavingsQuery,
  useGetEfficiencyTrendsQuery,
  useExportAnalyticsMutation,
  useGetDashboardSummaryQuery
} = routeAnalyticsApi;
